import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Textarea } from './textarea';
import { Input } from './input';

/**
 * DebouncedTextarea - A textarea that maintains local state and only
 * propagates changes to parent after a delay, preventing lag on large components.
 * 
 * Uses ref-based pending tracking to avoid race conditions between
 * child state updates and parent re-renders.
 */
export const DebouncedTextarea = React.memo(({ 
  value: externalValue, 
  onChange,
  onBlur: externalOnBlur,
  debounceMs = 300,
  autoGrow = false,
  ...props 
}) => {
  const [localValue, setLocalValue] = useState(externalValue || '');
  const hasPendingRef = useRef(false);
  
  const timeoutRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const lastExternalRef = useRef(externalValue || '');
  // Mirror the latest local value into a ref so unmount cleanup can
  // flush WITHOUT depending on stale closure values from useEffect.
  const lastLocalValueRef = useRef(externalValue || '');
  const textareaRef = useRef(null);
  
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // Auto-grow
  useEffect(() => {
    if (autoGrow && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [localValue, autoGrow]);
  
  // Sync external value changes (only when NOT pending a local update)
  useEffect(() => {
    const ext = externalValue ?? '';
    if (ext !== lastExternalRef.current && !hasPendingRef.current) {
      lastExternalRef.current = ext;
      lastLocalValueRef.current = ext;
      setLocalValue(ext);
    }
  }, [externalValue]);
  
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    lastLocalValueRef.current = newValue;
    hasPendingRef.current = true;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      hasPendingRef.current = false;
      lastExternalRef.current = newValue;
      if (onChangeRef.current) {
        onChangeRef.current({ target: { value: newValue } });
      }
    }, debounceMs);
  }, [debounceMs]);
  
  const handleBlur = useCallback((e) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hasPendingRef.current) {
      hasPendingRef.current = false;
      lastExternalRef.current = localValue;
      if (onChangeRef.current) {
        onChangeRef.current({ target: { value: localValue } });
      }
    }
    if (externalOnBlur) {
      externalOnBlur(e);
    }
  }, [localValue, externalOnBlur]);
  
  // Flush on unmount — guarantees zero data loss if the textarea is
  // unmounted (e.g., dialog closed via Escape, parent conditionally
  // hidden, page navigation) before the debounce timeout fires.
  // Without this, a user who types and then triggers an unmount without
  // first losing focus could lose their pending input.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Use refs (NOT closure-captured `localValue` / `onChange`) so we
      // flush whatever the user actually typed last, not a stale value
      // captured at effect-mount time.
      if (hasPendingRef.current && onChangeRef.current) {
        const finalValue = lastLocalValueRef.current;
        try {
          onChangeRef.current({ target: { value: finalValue } });
        } catch (e) {
          // Synchronously firing onChange during unmount can throw if
          // the parent component is also tearing down. Better to log
          // than to break unmount, but log loudly so we notice in dev.
          console.warn('[DebouncedTextarea] flush-on-unmount onChange failed:', e);
        }
        hasPendingRef.current = false;
      }
    };
  }, []);
  
  return (
    <Textarea
      {...props}
      ref={textareaRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});

DebouncedTextarea.displayName = 'DebouncedTextarea';

/**
 * DebouncedInput - An input that maintains local state and only
 * propagates changes to parent after a delay.
 * 
 * Uses ref-based pending tracking to avoid race conditions.
 */
export const DebouncedInput = React.memo(({ 
  value: externalValue, 
  onChange, 
  onBlur: externalOnBlur,
  debounceMs = 300,
  className,
  ...props 
}) => {
  const [localValue, setLocalValue] = useState(externalValue || '');
  const hasPendingRef = useRef(false);
  
  const timeoutRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const lastExternalRef = useRef(externalValue || '');
  // Mirror latest local value for safe flush-on-unmount.
  const lastLocalValueRef = useRef(externalValue || '');
  
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // Sync external value changes (only when NOT pending a local update)
  useEffect(() => {
    const ext = externalValue ?? '';
    if (ext !== lastExternalRef.current && !hasPendingRef.current) {
      lastExternalRef.current = ext;
      lastLocalValueRef.current = ext;
      setLocalValue(ext);
    }
  }, [externalValue]);
  
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    lastLocalValueRef.current = newValue;
    hasPendingRef.current = true;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      hasPendingRef.current = false;
      lastExternalRef.current = newValue;
      if (onChangeRef.current) {
        onChangeRef.current({ target: { value: newValue } });
      }
    }, debounceMs);
  }, [debounceMs]);
  
  const handleBlur = useCallback((e) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hasPendingRef.current) {
      hasPendingRef.current = false;
      lastExternalRef.current = localValue;
      if (onChangeRef.current) {
        onChangeRef.current({ target: { value: localValue } });
      }
    }
    if (externalOnBlur) {
      externalOnBlur(e);
    }
  }, [localValue, externalOnBlur]);
  
  // Flush on unmount — see DebouncedTextarea above for full rationale.
  // Same pattern: prevents data loss if the input is unmounted (Escape,
  // navigation, conditional render) before the debounce fires.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (hasPendingRef.current && onChangeRef.current) {
        const finalValue = lastLocalValueRef.current;
        try {
          onChangeRef.current({ target: { value: finalValue } });
        } catch (e) {
          console.warn('[DebouncedInput] flush-on-unmount onChange failed:', e);
        }
        hasPendingRef.current = false;
      }
    };
  }, []);
  
  return (
    <Input
      {...props}
      className={className}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});

DebouncedInput.displayName = 'DebouncedInput';
